/**
 * O paciente do contato no SISAC ligado ao back (#1024): ler o vínculo,
 * procurar, vincular, desvincular e cadastrar.
 *
 * ── O CPF não fica guardado ──────────────────────────────────────────────
 *
 * A busca é mutação, e não consulta: o CPF e o nome não viram chave de cache,
 * e a mutação some da memória quando o card sai da tela (`gcTime: 0`).
 *
 * ── Um cadastro, uma chave ───────────────────────────────────────────────
 *
 * O cadastro exige `Idempotency-Key`. A chave nasce no primeiro envio e volta
 * no reenvio do mesmo formulário: se a primeira resposta se perdeu, o servidor
 * devolve a gravada em vez de cadastrar outra vez no SISAC. Mexer no formulário
 * troca a chave.
 */

import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ErroDaApi,
  type CadastrarPacienteDoCliente,
  type CandidatosAPaciente,
  type PacienteDoCliente,
} from '@cliqcrm/contracts';
import { useApi, usePacienteDoCliente } from '@/shared/api/consultas';
import { chaves, ehDaConsultaDeOportunidade } from '@/shared/api/chaves';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import type { PedidoDaBusca } from '../lib/busca';

const SISAC_FORA = 'O SISAC não respondeu. Tente de novo em instantes.';

const foraDoAr = (erro: unknown) => erro instanceof ErroDaApi && erro.status === 503;

export const recadoDoSisac = (erro: unknown, generica: string): string =>
  foraDoAr(erro) ? SISAC_FORA : recadoDeErro(erro, { generica });

const novaChave = () =>
  globalThis.crypto?.randomUUID?.() ??
  `cadastrar-${Date.now()}-${Math.random().toString(16).slice(2)}`;

export function usePacienteNoSisac(clienteId: string, ligada: boolean) {
  const api = useApi();
  const cache = useQueryClient();
  const leitura = usePacienteDoCliente(clienteId, ligada);
  const [aviso, definirAviso] = useState<string | null>(null);
  const chaveDoCadastro = useRef<{ corpo: string; chave: string } | null>(null);

  const guardar = (paciente: PacienteDoCliente, recado: string) => {
    cache.setQueryData(chaves.pacienteDoCliente(clienteId), paciente);
    // A aba Consulta das oportunidades deste contato passa a ver (ou não) o vinculado.
    void cache.invalidateQueries({
      predicate: (consulta) => ehDaConsultaDeOportunidade(consulta.queryKey),
    });
    // O prontuário é lido pelo paciente do vínculo (#1144): trocou o vínculo, relê.
    void cache.invalidateQueries({ queryKey: chaves.prontuarioDoPaciente(clienteId) });
    definirAviso(recado);
  };

  const busca = useMutation({
    gcTime: 0,
    mutationFn: async (pedido: PedidoDaBusca): Promise<CandidatosAPaciente> => {
      if (!api) throw new Error('Sem sessão.');
      return api.buscarPacienteDoCliente({ caminho: { id: clienteId }, corpo: pedido });
    },
    onMutate: () => definirAviso(null),
  });

  const vincular = useMutation({
    mutationFn: async (codigo: string) => {
      if (!api) throw new Error('Sem sessão.');
      return api.vincularPacienteDoCliente({ caminho: { id: clienteId }, corpo: { codigo } });
    },
    onSuccess: (paciente) => {
      busca.reset();
      guardar(paciente, 'Paciente vinculado.');
    },
  });

  const desvincular = useMutation({
    mutationFn: async () => {
      if (!api) throw new Error('Sem sessão.');
      return api.desvincularPacienteDoCliente({ caminho: { id: clienteId } });
    },
    onSuccess: () => {
      const atual = cache.getQueryData<PacienteDoCliente>(chaves.pacienteDoCliente(clienteId));
      if (atual) {
        guardar(
          { ...atual, vinculo: null, paciente: null, consultas: [], sisac: 'ok' },
          'Vínculo desfeito. Nada mudou no SISAC.',
        );
      }
    },
  });

  const cadastrar = useMutation({
    mutationFn: async (corpo: CadastrarPacienteDoCliente) => {
      if (!api) throw new Error('Sem sessão.');
      const assinatura = JSON.stringify(corpo);
      if (chaveDoCadastro.current?.corpo !== assinatura) {
        chaveDoCadastro.current = { corpo: assinatura, chave: novaChave() };
      }
      return api.cadastrarPacienteDoCliente({
        caminho: { id: clienteId },
        corpo,
        idempotencyKey: chaveDoCadastro.current.chave,
      });
    },
    onSuccess: (paciente) => {
      chaveDoCadastro.current = null;
      busca.reset();
      guardar(paciente, 'Paciente cadastrado no SISAC e vinculado.');
    },
  });

  return {
    leitura,
    aviso,
    busca,
    vincular,
    desvincular,
    cadastrar,
    limparAviso: () => definirAviso(null),
  };
}
